/*
 * Which of an email's fields the rep may change, per sending route. The composer draws every field
 * as the same inline field in every mode; one this map closes is drawn the same way but read-only,
 * and says why on hover, on focus and, on a phone, on a tap (Dawid, 2026-10-23: "make the field non
 * editable but design editable for future direct mailbox connection support"). A transport added
 * later (a mailbox connected directly) is a new column here, not a change to the composer.
 *
 * Email Bison is today's only transport. Its live reference (checked 2026-10-23) accepts To, Cc and
 * Bcc arrays, a sender, the body, its content type and files on both its reply and its forward
 * route, and no subject on either (the design doc's field matrix names them). Where a field is closed by LeadCue's own send path rather
 * than by Bison, the reason says that instead (docs/2026-10-23-unified-composer-design.md).
 */

/** `originals`: the files of the email a forward forwards, or a reply answers. */
export type ComposeField = "to" | "cc" | "bcc" | "from" | "subject" | "originals";
export type ComposeRoute = "reply" | "forward";
export type Transport = "bison";

export type FieldCapability =
  | { readonly editable: true }
  | { readonly editable: false; readonly reason: string };

const OPEN: FieldCapability = { editable: true };
const closed = (reason: string): FieldCapability => ({ editable: false, reason });

// What Bison delivers is not on record for LeadCue's own sends (2026-10-23), so the field shows the
// subject it starts from and says that Bison sets the delivered one.
const BISON_SUBJECT = closed(
  "Email Bison sets the delivered subject from this one. Email Bison inboxes don't allow changing it.",
);
// Dawid, 2026-10-23 (ledger 818ad01e): "Keep fixed, disabled + note"; a direct mailbox may open it.
const BISON_FROM = closed(
  "Email Bison inboxes send from the conversation's own mailbox, so the sender can't be changed.",
);
// Bison's forward route attaches the forwarded email's own files whatever LeadCue sends: one upload
// reached the recipient twice (QF4, 2026-10-23). Its reply route attached none of a parent's file
// (P-B, the same day). So a forward shows them fixed, and LeadCue never uploads a copy.
const BISON_ORIGINALS = closed(
  "Email Bison attaches the original email's files to every forward. Email Bison inboxes don't allow changing this.",
);
const REPLY_ORIGINALS = closed(
  "A reply carries only the files you attach. Email Bison inboxes don't allow changing this.",
);

// Both routes carry the rep's To, Cc and Bcc, frozen with the send (`InboxSendRequest`).
const CAPABILITIES: Record<
  Transport,
  Record<ComposeRoute, Record<ComposeField, FieldCapability>>
> = {
  bison: {
    reply: {
      to: OPEN,
      cc: OPEN,
      bcc: OPEN,
      from: BISON_FROM,
      subject: BISON_SUBJECT,
      originals: REPLY_ORIGINALS,
    },
    forward: {
      to: OPEN,
      cc: OPEN,
      bcc: OPEN,
      from: BISON_FROM,
      subject: BISON_SUBJECT,
      originals: BISON_ORIGINALS,
    },
  },
};

export function fieldCapability(
  route: ComposeRoute,
  field: ComposeField,
  transport: Transport = "bison",
): FieldCapability {
  return CAPABILITIES[transport][route][field];
}
